import classnames from 'classnames'
import ResultsList from 'components/ResultsList/ResultsList'
import {
  FiltersWrapper,
  ResultListWrapper,
  SortWrapperForMobile,
} from 'components/SearchResultWrappers/SearchResultWrappers'
import {
  Age,
  FilterType,
  ProductType,
  RoutePathname,
  SearchResponseType,
} from 'frontend/enums'
import Api from 'frontend/lib/Api'
import {
  BlockTracking,
  FilterProp,
  Filters,
  Program,
  SearchResult,
} from 'frontend/types'
import useErrorRedirect from 'frontend/utils/hooks/useErrorRedirect'
import { removeEmptyKeys } from 'frontend/utils/urlHelpers'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import MobileFilters from '../MobileFilters/MobileFilters'
import Format from './components/HorizontalFilters/components/Format/Format'
import HorizontalFilters from './components/HorizontalFilters/HorizontalFilters'
import styles from './HorizontalFilterContainer.module.css'
import SearchTags from './components/HorizontalFilters/components/SearchTags/SearchTags'
import searchTagsAdapter from 'backend/lib/adapters/searchTagsAdapter'
import FilterHeader, {
  FilterHeaderProps,
} from 'components/HorizontalFilterContainer/components/FilterHeader/FilterHeader'
import { SearchParams } from 'backend/services/types/urApiService.types'
import Sort from './components/HorizontalFilters/components/Sort'
import { useRouter } from 'next/router'

interface Props {
  initialSearchResult: SearchResult
  initialSearchParams: SearchParams
  children?: React.ReactNode
  includeRelevance?: boolean
  hiddenFilters?: FilterType[]
  blockTracking: BlockTracking
  filterHeader?: FilterHeaderProps
  excludedTags?: string[]
  isChildPage?: boolean
}

interface MobileFilterProps {
  filterHeader: FilterHeaderProps
  totalCount: number
  filters: Filters
  hiddenFilters: FilterType[]
  filterSearchParams: SearchParams
  updateSearch: (params: SearchParams) => Promise<void>
  includeRelevance: boolean
  isFilterButtonHighlighted: boolean
}

const MobileFiltersContainer = ({
  filterHeader,
  totalCount,
  filters,
  hiddenFilters,
  filterSearchParams,
  updateSearch,
  includeRelevance,
  isFilterButtonHighlighted,
}: MobileFilterProps) => (
  <div className={styles.hideOnDesktop}>
    <FiltersWrapper>
      <FilterHeader {...filterHeader} />
    </FiltersWrapper>

    <SortWrapperForMobile>
      <MobileFilters
        totalCount={totalCount}
        filters={filters}
        hiddenFilters={hiddenFilters}
        searchParams={filterSearchParams}
        updateSearch={updateSearch}
        isFilterButtonHighlighted={isFilterButtonHighlighted}
      />
      {!hiddenFilters.includes(FilterType.SORT) && (
        <Sort
          searchParams={filterSearchParams}
          updateSearch={updateSearch}
          includeRelevance={includeRelevance}
          isOnMobile
        />
      )}
    </SortWrapperForMobile>
  </div>
)

const defaultFilters = {
  languages: [],
  educationLevels: [],
  subjectTree: [],
  hasAudioDescription: false,
  hasSignLanguageInterpretation: false,
  formats: [],
  teacherResources: [],
  subjectTreeExtended: [],
  knowledgeAreaExtended: [],
}

const HorizontalFilterContainer = ({
  initialSearchResult,
  initialSearchParams,
  blockTracking,
  includeRelevance = false,
  hiddenFilters = [],
  filterHeader,
  excludedTags = [],
  isChildPage = false,
}: Props) => {
  const router = useRouter()
  const isOnEducationalPage = router.pathname === RoutePathname.EDUCATION_LEVEL
  const isOnChildPage = router.pathname === RoutePathname.CHILD_AGE_RANGE

  const [count, setCount] = useState(initialSearchResult.count)
  const [filters, setFilters] = useState(initialSearchResult.filters)
  const [filterSearchParams, setFilterSearchParams] = useState({
    product_type: (initialSearchParams.product_type ?? '') as ProductType,
    ...initialSearchParams,
  } as SearchParams)
  const [isSwitchingFilters, setIsSwitchingFilters] = useState(false)

  const [productResults, setProductResults] = useState(
    initialSearchResult.results,
  )
  const [nextPageInfo, setNextPageInfo] = useState(
    initialSearchResult.nextPageInfo,
  )

  const defaultCount = {
    audio: 0,
    video: 0,
    total: 0,
    searchTotal: 0,
  }

  const [searchTags, setSearchTags] = useState(
    searchTagsAdapter.adapt(
      initialSearchResult.filters || defaultFilters,
      initialSearchParams,
      excludedTags,
    ),
  )

  useEffect(() => {
    setProductResults(initialSearchResult.results)
    setCount(initialSearchResult.count)
    setFilters(initialSearchResult.filters)
    setNextPageInfo(initialSearchResult.nextPageInfo)
  }, [initialSearchResult])

  useEffect(() => {
    setFilterSearchParams({
      product_type: (initialSearchParams.product_type ?? '') as ProductType,
      ...initialSearchParams,
    } as SearchParams)

    setSearchTags(
      searchTagsAdapter.adapt(
        initialSearchResult.filters || defaultFilters,
        initialSearchParams,
        excludedTags,
      ),
    )
  }, [initialSearchParams])

  const resetFilters = {
    has_teacher_resource: null,
    is_audio_described: null,
    is_sign_language_interpreted: null,
    format: null,
    language: [],
    ...(!isOnEducationalPage &&
      !isOnChildPage && {
        typical_age_range: [],
      }),
    subject_tree: [],
    teacher_resource_type: [],
  }

  const updateUrlSearchParams = (filterParams: SearchParams) => {
    const currentPath = window.location.pathname

    const formattedFilterString = queryString.stringify(filterParams, {
      arrayFormat: 'bracket',
    })

    router.push(
      {
        pathname: currentPath,
        query: formattedFilterString,
      },
      undefined,
      { shallow: true },
    )
  }

  const sendErrorIntoComponentTree = useErrorRedirect()

  const multipleChoiceHandler = (
    nextFilterSearchParams: SearchParams,
    key: keyof SearchParams,
  ): Array<string | number | boolean | string[]> => {
    const previousSearchParams = filterSearchParams[key]

    const previousSearchParamArray = Array.isArray(previousSearchParams)
      ? previousSearchParams
      : [previousSearchParams]

    const incomingSearchParams = nextFilterSearchParams[key]

    if (Array.isArray(incomingSearchParams)) {
      const filtersToAdd = incomingSearchParams.filter(
        param => !previousSearchParamArray.includes(param),
      )

      const filtersToKeep = previousSearchParamArray.filter(
        (param): param is string =>
          typeof param === 'string' &&
          !(incomingSearchParams as string[]).includes(param),
      )

      const nextSelection = [...filtersToKeep, ...filtersToAdd]

      return nextSelection.filter(Boolean)
    } else {
      const shouldDeselect = previousSearchParamArray.includes(
        nextFilterSearchParams[key] as string,
      )

      const nextSelection = shouldDeselect
        ? previousSearchParamArray.filter(
            option => option !== nextFilterSearchParams[key],
          )
        : [...previousSearchParamArray, nextFilterSearchParams[key]]

      return nextSelection.filter(Boolean) as (
        | string
        | number
        | boolean
        | string[]
      )[]
    }
  }

  const prepareParams = (
    nextFilterSearchParams: SearchParams,
    shouldResetAllFilters = false,
  ): SearchParams => {
    const nextLanguages = multipleChoiceHandler(
      nextFilterSearchParams,
      'language',
    )

    const nextAgeRanges = multipleChoiceHandler(
      nextFilterSearchParams,
      'typical_age_range',
    ).filter(option => option !== Age.CHILDREN)

    const nextSubjectTree = multipleChoiceHandler(
      nextFilterSearchParams,
      'subject_tree',
    )

    const nextTeacherResourceType = multipleChoiceHandler(
      nextFilterSearchParams,
      'teacher_resource_type',
    )

    return removeEmptyKeys({
      ...filterSearchParams,
      ...nextFilterSearchParams,
      language: nextLanguages,
      typical_age_range: nextAgeRanges,
      subject_tree: nextSubjectTree,
      teacher_resource_type: nextTeacherResourceType,
      ...(shouldResetAllFilters && resetFilters),
    })
  }

  const updateSearch = async (
    nextFilterSearchParams: SearchParams,
    shouldResetAllFilters = false,
  ) => {
    setIsSwitchingFilters(true)

    const nextSearchParams = prepareParams(
      nextFilterSearchParams,
      shouldResetAllFilters,
    )

    const nextSearchTags = searchTagsAdapter.adapt(
      filters || defaultFilters,
      nextSearchParams,
      excludedTags,
    )
    setSearchTags(nextSearchTags)

    setFilterSearchParams(nextSearchParams)
    updateUrlSearchParams(nextSearchParams)

    try {
      const searchResult = await Api.getSearchResults({
        ...nextSearchParams,
        response_type: SearchResponseType.EXTENDED,
      })
      setNextPageInfo(searchResult.nextPageInfo)
      setProductResults(searchResult.results)
      setFilters(searchResult.filters)
      setCount(searchResult.count)
    } catch (error) {
      sendErrorIntoComponentTree(error)
    } finally {
      setIsSwitchingFilters(false)
    }
  }

  const loadNextPage = async () => {
    try {
      const searchResult = await Api.getSearchResults({
        ...filterSearchParams,
        ...nextPageInfo,
        response_type: SearchResponseType.LIMITED,
      })
      setNextPageInfo(searchResult.nextPageInfo)
      setProductResults([...productResults, ...searchResult.results])
    } catch (error) {
      sendErrorIntoComponentTree(error)
    }
  }

  const isMobileFilterHighlighted =
    searchTags.length > 0 || Boolean(filterSearchParams.format)

  return (
    <>
      {filters && (
        <MobileFiltersContainer
          filterSearchParams={filterSearchParams}
          filters={filters}
          hiddenFilters={hiddenFilters}
          includeRelevance={includeRelevance}
          filterHeader={filterHeader || { headingLevel: 'h2' }}
          totalCount={count?.searchTotal || 0}
          updateSearch={updateSearch}
          isFilterButtonHighlighted={isMobileFilterHighlighted}
        />
      )}

      <div
        className={classnames(styles.background, styles.hideOnMobile, {
          [`${styles.hiddenHeading}`]: filterHeader?.hideTitle,
        })}
        data-testid="desktop-filter"
      >
        <div className={styles.filterWrapper}>
          {filterHeader && <FilterHeader {...filterHeader} />}
          <HorizontalFilters
            isChildPage={isChildPage}
            filters={filters || defaultFilters}
            hiddenFilters={hiddenFilters}
            searchParams={filterSearchParams}
            updateSearch={updateSearch}
            includeRelevance={includeRelevance}
            count={count || defaultCount}
            filterSearchParams={filterSearchParams}
          />
        </div>
      </div>
      <div className={styles.tagWrapper}>
        {searchTags.length > 0 && (
          <SearchTags searchTags={searchTags} updateSearch={updateSearch} />
        )}
      </div>
      <div className={styles.background}>
        <ResultListWrapper
          data-testid="search-result-list"
          data-trk="subject-page-results-list"
          data-trk-block-type="carpet"
        >
          <div className={styles.resultsWrapper}>
            <div className={styles.desktopResultsHeader}>
              {!isChildPage && (
                <Format
                  formats={(filters?.formats as FilterProp[]) || defaultFilters}
                  count={count || defaultCount}
                  searchParams={filterSearchParams}
                  updateSearch={updateSearch}
                />
              )}
            </div>
            <ResultsList
              hasNextPage={Boolean(nextPageInfo)}
              showPlaceholders={isSwitchingFilters}
              loadNextPage={loadNextPage}
              products={productResults as Program[]}
              blockTracking={blockTracking}
            />
          </div>
        </ResultListWrapper>
      </div>
    </>
  )
}

export default HorizontalFilterContainer
